import { getSupabaseClient } from "../supabase/index.js";
import { defaultBillingCorrectionReasonEntries } from "./billing-correction-reasons.js";
import { defaultPaymentEntryMethodEntries } from "./payment-entry-methods.js";
import {
  defaultAttendanceDisputeClaimReasonEntries,
  defaultBillingStatementDisputeClaimReasonEntries,
  defaultDisputeAcceptReasonEntries,
  defaultDisputeRejectReasonEntries,
} from "./dispute-reference-lists.js";
import { normalizeReferenceListEntriesArray } from "./reference-list-entries.js";
import { buildReferenceListAuditRows } from "./reference-list-audit.js";
import {
  loadReferenceListUsedIds,
  protectReferenceListsOnSave,
} from "./reference-list-usage.js";
import { defaultSessionCancelReasonEntries } from "./session-cancel-reasons.js";
import {
  defaultSessionCourtFormatEntries,
  defaultSessionTargetGroupEntries,
} from "./session-planning-reference-lists.js";
import {
  defaultBillingFrequencyEntries,
  defaultBillingPaymentTimingEntries,
  defaultTrainerAbsenceReasonEntries,
  defaultTrainerPayoutFrequencyEntries,
  defaultTrainerPayoutMethodEntries,
} from "./trainer-stammdaten-reference-lists.js";

/**
 * @typedef {import('./reference-list-entries.js').ReferenceListEntry} ReferenceListEntry
 * @typedef {{
 *   sessionCancelReasons?: ReferenceListEntry[],
 *   billingCorrectionReasons?: ReferenceListEntry[],
 *   paymentEntryMethods?: ReferenceListEntry[],
 *   attendanceDisputeClaimReasons?: ReferenceListEntry[],
 *   disputeAcceptReasons?: ReferenceListEntry[],
 *   disputeRejectReasons?: ReferenceListEntry[],
 *   billingStatementDisputeClaimReasons?: ReferenceListEntry[],
 *   sessionTargetGroups?: ReferenceListEntry[],
 *   sessionCourtFormats?: ReferenceListEntry[],
 *   trainerAbsenceReasons?: ReferenceListEntry[],
 *   billingFrequencies?: ReferenceListEntry[],
 *   billingPaymentTimings?: ReferenceListEntry[],
 *   trainerPayoutFrequencies?: ReferenceListEntry[],
 *   trainerPayoutMethods?: ReferenceListEntry[],
 * }} TenantReferenceLists
 */

function asStr(v) {
  return String(v ?? "").trim();
}

/**
 * @param {unknown} error
 * @returns {string} i18n key
 */
export function resolveReferenceListSaveErrorKey(error) {
  if (!error) return "trainerstunden.referenceLists.saveFailed";
  if (typeof error === "string") {
    if (error === "missing-tenant")
      return "trainerstunden.referenceLists.saveMissingTenant";
    if (error === "no-supabase")
      return "trainerstunden.referenceLists.saveNoSupabase";
    if (error === "table-missing" || error === "schema-missing") {
      return "trainerstunden.referenceLists.saveSchemaMissing";
    }
    return "trainerstunden.referenceLists.saveFailed";
  }
  const code = asStr(error?.code);
  const message = asStr(error?.message);
  if (code === "42P01" || code === "42703")
    return "trainerstunden.referenceLists.saveSchemaMissing";
  if (
    code === "42501" ||
    /permission|policy|row-level security/i.test(message)
  ) {
    return "trainerstunden.referenceLists.savePermissionDenied";
  }
  if (code === "23503")
    return "trainerstunden.referenceLists.saveInvalidTenant";
  return "trainerstunden.referenceLists.saveFailed";
}

function normalizeListField(raw, fallback) {
  const normalized = normalizeReferenceListEntriesArray(raw);
  return normalized?.length ? normalized : fallback;
}

/**
 * @returns {TenantReferenceLists}
 */
export function defaultTenantReferenceLists() {
  return {
    sessionCancelReasons: defaultSessionCancelReasonEntries(),
    billingCorrectionReasons: defaultBillingCorrectionReasonEntries(),
    paymentEntryMethods: defaultPaymentEntryMethodEntries(),
    attendanceDisputeClaimReasons: defaultAttendanceDisputeClaimReasonEntries(),
    disputeAcceptReasons: defaultDisputeAcceptReasonEntries(),
    disputeRejectReasons: defaultDisputeRejectReasonEntries(),
    billingStatementDisputeClaimReasons:
      defaultBillingStatementDisputeClaimReasonEntries(),
    sessionTargetGroups: [...defaultSessionTargetGroupEntries],
    sessionCourtFormats: defaultSessionCourtFormatEntries(),
    trainerAbsenceReasons: defaultTrainerAbsenceReasonEntries(),
    billingFrequencies: defaultBillingFrequencyEntries(),
    billingPaymentTimings: defaultBillingPaymentTimingEntries(),
    trainerPayoutFrequencies: defaultTrainerPayoutFrequencyEntries(),
    trainerPayoutMethods: defaultTrainerPayoutMethodEntries(),
  };
}

/**
 * @param {unknown} raw
 * @returns {TenantReferenceLists}
 */
export function normalizeTenantReferenceLists(raw) {
  const base = defaultTenantReferenceLists();
  if (!raw || typeof raw !== "object") return base;
  return {
    sessionCancelReasons: normalizeListField(
      raw.sessionCancelReasons,
      base.sessionCancelReasons,
    ),
    billingCorrectionReasons: normalizeListField(
      raw.billingCorrectionReasons,
      base.billingCorrectionReasons,
    ),
    paymentEntryMethods: normalizeListField(
      raw.paymentEntryMethods,
      base.paymentEntryMethods,
    ),
    attendanceDisputeClaimReasons: normalizeListField(
      raw.attendanceDisputeClaimReasons,
      base.attendanceDisputeClaimReasons,
    ),
    disputeAcceptReasons: normalizeListField(
      raw.disputeAcceptReasons,
      base.disputeAcceptReasons,
    ),
    disputeRejectReasons: normalizeListField(
      raw.disputeRejectReasons,
      base.disputeRejectReasons,
    ),
    billingStatementDisputeClaimReasons: normalizeListField(
      raw.billingStatementDisputeClaimReasons,
      base.billingStatementDisputeClaimReasons,
    ),
    sessionTargetGroups: normalizeListField(
      raw.sessionTargetGroups,
      base.sessionTargetGroups,
    ),
    sessionCourtFormats: normalizeListField(
      raw.sessionCourtFormats,
      base.sessionCourtFormats,
    ),
    trainerAbsenceReasons: normalizeListField(
      raw.trainerAbsenceReasons,
      base.trainerAbsenceReasons,
    ),
    billingFrequencies: normalizeListField(
      raw.billingFrequencies,
      base.billingFrequencies,
    ),
    billingPaymentTimings: normalizeListField(
      raw.billingPaymentTimings,
      base.billingPaymentTimings,
    ),
    trainerPayoutFrequencies: normalizeListField(
      raw.trainerPayoutFrequencies,
      base.trainerPayoutFrequencies,
    ),
    trainerPayoutMethods: normalizeListField(
      raw.trainerPayoutMethods,
      base.trainerPayoutMethods,
    ),
  };
}

/**
 * @param {string} tenantId
 */
export async function loadTenantReferenceLists(tenantId) {
  const tid = asStr(tenantId);
  if (!tid)
    return {
      ok: true,
      data: defaultTenantReferenceLists(),
      reason: "missing-tenant",
    };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const { data, error } = await supabase
      .from("trainerstunden_tenant_reference_lists")
      .select("lists")
      .eq("tenant_id", tid)
      .maybeSingle();

    if (error) {
      const code = String(error?.code || "");
      if (code === "42P01") {
        return {
          ok: true,
          data: defaultTenantReferenceLists(),
          reason: "table-missing",
        };
      }
      return { ok: false, error };
    }

    return {
      ok: true,
      data: normalizeTenantReferenceLists(data?.lists),
    };
  } catch (e) {
    return { ok: false, error: e };
  }
}

/**
 * @param {string} tenantId
 * @param {TenantReferenceLists} lists
 * @param {{ previousLists?: TenantReferenceLists }} [options]
 */
export async function saveTenantReferenceLists(tenantId, lists, options = {}) {
  const tid = asStr(tenantId);
  if (!tid) return { ok: false, error: "missing-tenant" };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    let previousLists = options.previousLists;
    if (!previousLists) {
      const existing = await loadTenantReferenceLists(tid);
      previousLists = existing.ok
        ? existing.data
        : defaultTenantReferenceLists();
    }

    const usedIdsByKey = await loadReferenceListUsedIds(tid, previousLists);
    const payload = protectReferenceListsOnSave(
      lists,
      previousLists,
      usedIdsByKey,
    );

    const { data: existingRow, error: readError } = await supabase
      .from("trainerstunden_tenant_reference_lists")
      .select("tenant_id, settings")
      .eq("tenant_id", tid)
      .maybeSingle();

    if (readError) {
      const readCode = String(readError?.code || "");
      if (readCode === "42P01") return { ok: false, error: "table-missing" };
      if (readCode !== "42703") return { ok: false, error: readError };
    }

    /** @type {Record<string, unknown>} */
    const row = {
      tenant_id: tid,
      lists: payload,
      updated_at: new Date().toISOString(),
    };
    if (!readError || String(readError?.code || "") !== "42703") {
      row.settings = existingRow?.settings ?? {};
    }

    const { error } = await supabase
      .from("trainerstunden_tenant_reference_lists")
      .upsert(row, {
        onConflict: "tenant_id",
      });

    if (error) {
      const code = String(error?.code || "");
      if (code === "42P01") return { ok: false, error: "table-missing" };
      if (code === "42703") return { ok: false, error: "schema-missing" };
      return { ok: false, error };
    }

    const auditRows = buildReferenceListAuditRows(tid, previousLists, payload);
    if (auditRows.length) {
      const { error: auditError } = await supabase
        .from("trainerstunden_tenant_reference_list_audit")
        .insert(auditRows);
      if (auditError) {
        const auditCode = String(auditError?.code || "");
        if (auditCode !== "42P01" && auditCode !== "PGRST205") {
          return { ok: false, error: auditError };
        }
      }
    }

    return { ok: true, data: payload };
  } catch (e) {
    return { ok: false, error: e };
  }
}
